import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import ConfirmModal from '../../components/modals/ConfirmModal'

interface Specification {
    _id: string;
    name: string;
    details: string;
    icon: string;
}

export default function AdminSpecifications() {
    const { token } = useAuth()
    const [specifications, setSpecifications] = useState<Specification[]>([])
    const [loading, setLoading] = useState(true)
    const [name, setName] = useState('')
    const [details, setDetails] = useState('')
    const [image, setImage] = useState<File | null>(null)
    const [preview, setPreview] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; id: string; name: string }>({ isOpen: false, id: '', name: '' });

    useEffect(() => {
        fetchSpecifications()
    }, [])

    const fetchSpecifications = async () => {
        try {
            const res = await fetch(`${API_BASE}/specifications`)
            const data = await res.json()
            if (Array.isArray(data)) setSpecifications(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setImage(file)
            setPreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name || (!image && !editId) || !token) return
        setSubmitting(true)
        setMsg(null)

        let imageUrl = preview

        try {
            if (image) {
                const fd = new FormData()
                fd.append('image', image)
                const uploadRes = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const uploadData = await uploadRes.json()
                if (uploadData.url) imageUrl = uploadData.url
                else throw new Error('Image upload failed')
            }

            // 2. Save/Update specification
            const url = editId ? `${API_BASE}/specifications/${editId}` : `${API_BASE}/specifications`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ name, details, icon: imageUrl })
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editId ? 'Specification updated successfully!' : 'Specification added successfully!' })
                setName('')
                setDetails('')
                setImage(null)
                setPreview('')
                setEditId(null)
                setIsModalOpen(false)
                fetchSpecifications()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || 'Failed to add specification' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async () => {
        const { id } = confirmModal;
        if (!token || !id) return
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await fetch(`${API_BASE}/specifications/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setSpecifications(prev => prev.filter(a => a._id !== id))
                setMsg({ type: 'success', text: 'Specification deleted.' })
            }
        } catch (err) {
            console.error(err)
        }
    }

    const handleEdit = (s: Specification) => {
        setName(s.name)
        setDetails(s.details || '')
        setPreview(s.icon)
        setImage(null)
        setEditId(s._id)
        setMsg(null)
        setIsModalOpen(true)
    }

    return (
        <AdminLayout>
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#05144b] mb-2 leading-none">Specifications Registry</h1>
                    <p className="text-[#05144b] font-semibold text-base leading-relaxed">Manage and maintain property specification standards.</p>
                </div>
                <button 
                    onClick={() => {
                        setEditId(null)
                        setName('')
                        setDetails('')
                        setImage(null)
                        setPreview('')
                        setIsModalOpen(true)
                    }}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold transition-all shadow-xl active:scale-95"
                >
                    <i className="fa-solid fa-plus-circle"></i>
                    <span>Add Specification</span>
                </button>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-md flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
                    msg.type === 'success' 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                        : 'bg-rose-50 text-rose-700 border border-rose-100'
                }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i>
                    <p className="text-sm font-bold">{msg.text}</p>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden min-h-[500px]">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50">
                    <div>
                        <h2 className="text-xl font-bold text-[#05144b]">Specification List</h2>
                        <p className="text-sm font-semibold text-[#05144b]/60 mt-1">{specifications.length} specifications available</p>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-[#05144b]">
                        <div className="animate-spin w-8 h-8 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-sm font-bold">Synchronizing specifications...</p>
                    </div>
                ) : specifications.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-white dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fa-solid fa-scroll text-black text-2xl"></i>
                        </div>
                        <p className="text-black font-bold">No Specifications Found</p>
                        <p className="text-[12px] text-black/60 mt-1 font-bold">Add your first specification to get started</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="px-8 py-5 text-left text-sm font-bold text-black border-b border-gray-100">Icon</th>
                                    <th className="px-8 py-5 text-left text-sm font-bold text-black border-b border-gray-100">Specification Name</th>
                                    <th className="px-8 py-5 text-left text-sm font-bold text-black border-b border-gray-100">Default Details</th>
                                    <th className="px-8 py-5 text-right text-sm font-bold text-black border-b border-gray-100">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {specifications.map(s => (
                                    <tr key={s._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-3">
                                            <div className="w-12 h-12 rounded-md bg-white p-2 flex items-center justify-center border border-slate-50 shadow-sm group-hover:shadow-md transition-all">
                                                <img 
                                                    src={getImageUrl(s.icon)} 
                                                    alt={s.name} 
                                                    className="w-full h-full object-contain" 
                                                />
                                            </div>
                                        </td>
                                        <td className="px-8 py-3">
                                            <span className="text-base font-bold text-black">{s.name}</span>
                                        </td>
                                        <td className="px-8 py-3">
                                            <p className="text-[14px] font-bold text-black/60 line-clamp-1 max-w-md leading-relaxed">
                                                {s.details || 'No details provided'}
                                            </p>
                                        </td>
                                        <td className="px-8 py-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button 
                                                    className="w-9 h-9 rounded-md bg-white border border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-lg active:scale-95"
                                                    onClick={() => handleEdit(s)}
                                                >
                                                    <i className="fa-solid fa-pen-nib text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-9 h-9 rounded-md bg-white border border-blue-900/10 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm active:scale-95"
                                                    onClick={() => setConfirmModal({ isOpen: true, id: s._id, name: s.name })}
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal Dialog */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#05144b]/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-5xl rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-6 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-xl">
                                    <i className="fa-solid fa-scroll text-base"></i>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-black tracking-tight capitalize">
                                        {editId ? 'Edit Specification' : 'Add Specification'}
                                    </h2>
                                    <p className="text-sm font-bold text-black/60 leading-none">Specification configuration system</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-blue-50 transition-all text-blue-950"
                            >
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="md:col-span-1">
                                        <label className="block text-sm font-bold text-black mb-2">
                                            Specification Name
                                        </label>
                                         <input 
                                            type="text" 
                                            className="w-full px-4 py-1.5 rounded-md bg-white border-2 border-blue-900/10 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] text-blue-950 transition-all placeholder:text-blue-900 font-bold outline-none shadow-sm text-base"
                                            value={name} 
                                            onChange={e => setName(e.target.value)} 
                                            placeholder="Category name..." 
                                            required 
                                        />
                                    </div>

                                    <div className="md:col-span-1">
                                        <label className="block text-sm font-bold text-black mb-2">
                                            Description
                                        </label>
                                         <textarea 
                                            className="w-full px-4 py-1.5 rounded-md bg-white border-2 border-blue-900/10 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] text-blue-950 transition-all placeholder:text-blue-900 font-bold outline-none shadow-sm resize-none text-base"
                                            rows={1} 
                                            value={details} 
                                            onChange={e => setDetails(e.target.value)} 
                                            placeholder="Enter details..."
                                        ></textarea>
                                    </div>

                                    <div className="md:col-span-1">
                                        <label className="block text-sm font-bold text-black mb-2">
                                            Specification Icon
                                        </label>
                                        <div className="relative group">
                                             <div className={`
                                                relative h-[34px] rounded-md border-2 border-dashed transition-all flex items-center justify-between px-3 overflow-hidden
                                                ${preview ? 'border-[#05144b] bg-blue-50/10' : 'border-blue-900/10 dark:border-slate-800 hover:border-[#05144b]/30 bg-white'}
                                            `}>
                                                {preview ? (
                                                    <div className="flex items-center gap-2">
                                                        <img 
                                                            src={getImageUrl(preview)} 
                                                            alt="Preview" 
                                                            className="h-6 w-6 object-contain bg-white p-0.5 rounded-sm border border-slate-50"
                                                        />
                                                        <p className="text-[10px] font-black text-blue-950 italic">Switch</p>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <i className="fa-solid fa-cloud-arrow-up text-black text-xs"></i>
                                                        <p className="text-[10px] font-bold text-black">Upload</p>
                                                    </div>
                                                )}
                                                <input 
                                                    type="file" 
                                                    className="absolute inset-0 opacity-0 cursor-pointer z-20"
                                                    onChange={handleImageChange} 
                                                    required={!editId} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-6">
                                    <button 
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 py-4 text-[15px] font-black text-blue-950/50 hover:text-blue-950 transition-colors italic"
                                    >
                                        Discard
                                    </button>
                                         <button 
                                        className={`
                                            flex-[2] py-4 rounded-md font-bold shadow-2xl transition-all flex items-center justify-center gap-2 active:scale-95
                                            ${submitting 
                                                ? 'bg-[#05144b]/50 text-white cursor-not-allowed' 
                                                : 'bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50'}
                                        `}
                                        type="submit" 
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                             <>
                                                <div className="animate-spin h-5 w-5 border-2 border-[#05144b]/30 border-t-[#05144b] rounded-full"></div>
                                                <span>Synchronizing...</span>
                                             </>
                                        ) : (
                                            <>
                                                <i className={`fa-solid ${editId ? 'fa-pen-nib' : 'fa-check-circle'}`}></i>
                                                <span>{editId ? 'Update Specification' : 'Save Specification'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title="Delete Specification"
                message={`Are you sure you want to delete the specification "${confirmModal.name}"? This will remove it from all projects.`}
                onConfirm={handleDelete}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                confirmText="Delete Specification"
                cancelText="Cancel"
                type="danger"
            />
        </AdminLayout>
    )
}
